/**
 * Дерево категорий OSMIX v2 (проект на согласование 27.09.2026, миграция 134).
 *
 * Модель — как у Lamoda и Ozon: аудитория → тип → вид («Женщинам / Обувь /
 * Кроссовки и кеды»). Так же устроена шторка «Каталог» мобильного приложения
 * (Женщинам / Мужчинам / Детям, внутри — Одежда, Обувь, Аксессуары), поэтому
 * одно дерево обслуживает и витрину, и приложение, и кабинет, и консоль.
 *
 * Здесь — единственный источник правды для кода: сид пустой базы, моки
 * клиентов, рекомендованные ставки и перенос товаров из дерева v1. Миграция
 * `134_category_tree_v2.sql` повторяет то же дерево в SQL; тест
 * `categoryTreeV2.migration.test.ts` сверяет, что они не разошлись.
 *
 * Идентификаторы узлов, чей смысл сохранился, оставлены прежними (`cat-1`,
 * `cat-1-1`, `cat-7-2`…): на них ссылаются ставки комиссии, подборки ленты,
 * запросы покупателей и закладки клиентов.
 */
import type { SizeGridId } from "./size-grids"

export type CatalogSectionKey = "clothing" | "shoes" | "accessories" | "beauty" | "home" | "kids" | "other"
export type CatalogGenderKey = "female" | "male" | "unisex" | "kids"

/** Набор обязательных характеристик — ключ в `ATTRIBUTE_SETS`. */
export type AttributeSetKey =
  | "clothing"
  | "lingerie"
  | "shoes"
  | "accessories"
  | "jewelry"
  | "kids-clothing"
  | "toys"
  | "beauty"
  | "perfume"
  | "home"
  | "tableware"
  | "candles"
  | "furniture"

export type CategoryTreeNode = {
  id: string
  name: string
  section: CatalogSectionKey
  gender: CatalogGenderKey
  /** Размерная сетка узла; нет — наследуется от родителя. */
  sizeGrid?: SizeGridId
  /** Характеристики узла; нет — наследуются от родителя. */
  attributes?: AttributeSetKey
  /** Рекомендованная ставка комиссии, доля (0.17 = 17 %); нет — наследуется. */
  rate?: number
  /** Аналог у Lamoda / WB — для документа на согласование. */
  analog?: string
  children?: CategoryTreeNode[]
}

export type AttributeDefinition = {
  key: string
  label: string
  type: "text" | "number" | "boolean" | "select"
  required?: boolean
  options?: string[]
}

const SEASON: AttributeDefinition = {
  key: "season",
  label: "Сезон",
  type: "select",
  options: ["spring", "summer", "autumn", "winter", "all"],
}
const COUNTRY: AttributeDefinition = { key: "countryOfOrigin", label: "Страна изготовления", type: "text", required: true }
const COMPOSITION: AttributeDefinition = { key: "composition", label: "Состав", type: "text", required: true }
const CARE: AttributeDefinition = { key: "care", label: "Уход", type: "text" }
const MATERIAL: AttributeDefinition = { key: "material", label: "Материал", type: "text", required: true }
/**
 * У одежды «Пол» и «Сезон» обязательны (замечание дизайнера 13.10.2026,
 * миграция шлюза 265). «Пол» — text: список значений рисует форма товара.
 */
const GENDER: AttributeDefinition = { key: "gender", label: "Пол", type: "text", required: true }
const SEASON_REQUIRED: AttributeDefinition = { ...SEASON, required: true }

/**
 * Характеристики по группам. Ключи `season`, `countryOfOrigin`, `composition`
 * — те же, что форма кабинета уже показывает отдельными полями: схема лишь
 * делает их обязательными. Остальное — новые поля схемы категории.
 */
export const ATTRIBUTE_SETS: Record<AttributeSetKey, AttributeDefinition[]> = {
  clothing: [GENDER, COMPOSITION, COUNTRY, SEASON_REQUIRED, CARE],
  lingerie: [COMPOSITION, COUNTRY, CARE],
  shoes: [
    { key: "upperMaterial", label: "Материал верха", type: "text", required: true },
    { key: "liningMaterial", label: "Материал подкладки", type: "text" },
    { key: "soleMaterial", label: "Материал подошвы", type: "text" },
    COUNTRY,
    SEASON,
  ],
  accessories: [MATERIAL, COUNTRY],
  jewelry: [
    { key: "material", label: "Материал / металл", type: "text", required: true },
    { key: "inlay", label: "Вставка", type: "text" },
    COUNTRY,
  ],
  "kids-clothing": [
    GENDER,
    COMPOSITION,
    COUNTRY,
    { key: "ageRange", label: "Возраст", type: "text" },
    SEASON_REQUIRED,
  ],
  toys: [
    { key: "ageFrom", label: "Возраст от", type: "select", required: true, options: ["0+", "1+", "3+", "6+", "12+"] },
    MATERIAL,
    COUNTRY,
  ],
  beauty: [
    { key: "volume", label: "Объём / вес", type: "text", required: true },
    { key: "composition", label: "Состав (INCI)", type: "text", required: true },
    { key: "shelfLifeMonths", label: "Срок годности, мес.", type: "number", required: true },
    { key: "skinType", label: "Тип кожи / волос", type: "text" },
    COUNTRY,
  ],
  perfume: [
    { key: "volume", label: "Объём, мл", type: "text", required: true },
    { key: "fragranceFamily", label: "Семейство аромата", type: "text", required: true },
    { key: "concentration", label: "Концентрация", type: "select", options: ["parfum", "edp", "edt", "edc", "other"] },
    COUNTRY,
  ],
  home: [MATERIAL, COUNTRY, CARE],
  tableware: [
    MATERIAL,
    COUNTRY,
    { key: "dishwasherSafe", label: "Можно в посудомоечную машину", type: "boolean" },
  ],
  candles: [
    { key: "material", label: "Воск / основа", type: "text", required: true },
    { key: "aroma", label: "Аромат", type: "text", required: true },
    { key: "burnTimeHours", label: "Время горения, ч", type: "number" },
    COUNTRY,
  ],
  furniture: [MATERIAL, COUNTRY, { key: "assembly", label: "Нужна сборка", type: "boolean" }],
}

const leaf = (
  id: string,
  name: string,
  section: CatalogSectionKey,
  gender: CatalogGenderKey,
  extra: Partial<CategoryTreeNode> = {},
): CategoryTreeNode => ({ id, name, section, gender, ...extra })

export const CATEGORY_TREE_V2: CategoryTreeNode[] = [
  {
    id: "cat-1",
    name: "Женщинам",
    section: "clothing",
    gender: "female",
    analog: "Lamoda: Женщинам; WB: Женщинам",
    children: [
      {
        id: "cat-w-clothing",
        name: "Одежда",
        section: "clothing",
        gender: "female",
        sizeGrid: "clothing-women",
        attributes: "clothing",
        rate: 0.17,
        analog: "Lamoda: Женщинам / Одежда",
        children: [
          leaf("cat-1-1", "Платья", "clothing", "female", { analog: "Lamoda: Платья и сарафаны" }),
          leaf("cat-1-2", "Юбки", "clothing", "female", { analog: "Lamoda: Юбки" }),
          leaf("cat-1-3", "Блузки и рубашки", "clothing", "female", { analog: "Lamoda: Блузы и рубашки" }),
          leaf("cat-w-tshirts", "Футболки и топы", "clothing", "female", { analog: "Lamoda: Топы и майки, Футболки и поло" }),
          leaf("cat-w-knit", "Худи и свитеры", "clothing", "female", {
            analog: "Lamoda: Худи и свитшоты; Джемперы, свитеры и кардиганы",
          }),
          leaf("cat-w-pants", "Брюки и джинсы", "clothing", "female", { analog: "Lamoda: Брюки, Джинсы, Шорты" }),
          leaf("cat-1-4", "Верхняя одежда", "clothing", "female", { analog: "Lamoda: Верхняя одежда" }),
          leaf("cat-w-sport", "Спортивная одежда", "clothing", "female", { rate: 0.16, analog: "Lamoda: Спорт; Ozon: Одежда для спорта" }),
          leaf("cat-w-lingerie", "Бельё и купальники", "clothing", "female", {
            sizeGrid: "lingerie",
            attributes: "lingerie",
            rate: 0.16,
            analog: "Lamoda: Нижнее бельё, Купальники",
          }),
        ],
      },
      {
        id: "cat-w-shoes",
        name: "Обувь",
        section: "shoes",
        gender: "female",
        sizeGrid: "shoes-women",
        attributes: "shoes",
        rate: 0.16,
        analog: "Lamoda: Женщинам / Обувь; WB: Обувь / Женская",
        children: [
          leaf("cat-w-sneakers", "Кроссовки и кеды", "shoes", "female", { analog: "Lamoda: Кроссовки и кеды, Слипоны" }),
          leaf("cat-w-heels", "Туфли и балетки", "shoes", "female", { analog: "Lamoda: Туфли, Балетки, Лоферы, Сабо и мюли" }),
          leaf("cat-w-boots", "Ботинки и сапоги", "shoes", "female", { analog: "Lamoda: Ботинки, Ботильоны, Сапоги" }),
          leaf("cat-w-sandals", "Сандалии и шлёпанцы", "shoes", "female", { analog: "Lamoda: Сандалии, Босоножки" }),
        ],
      },
      {
        id: "cat-w-acc",
        name: "Аксессуары",
        section: "accessories",
        gender: "female",
        sizeGrid: "one-size",
        attributes: "accessories",
        rate: 0.18,
        analog: "Lamoda: Женщинам / Аксессуары",
        children: [
          leaf("cat-w-bags", "Сумки и рюкзаки", "accessories", "female", { analog: "Lamoda: Сумки, Рюкзаки" }),
          leaf("cat-w-jewelry", "Украшения", "accessories", "female", {
            sizeGrid: "jewelry",
            attributes: "jewelry",
            analog: "Lamoda: Украшения; WB: Бижутерия",
          }),
          leaf("cat-w-hats", "Головные уборы и шарфы", "accessories", "female", {
            analog: "Lamoda: Головные уборы, Платки и шарфы",
          }),
          leaf("cat-w-acc-other", "Ремни, очки и другое", "accessories", "female", {
            analog: "Lamoda: Ремни и пояса, Очки, Кошельки, Чехлы",
          }),
        ],
      },
    ],
  },
  {
    id: "cat-2",
    name: "Мужчинам",
    section: "clothing",
    gender: "male",
    analog: "Lamoda: Мужчинам; WB: Мужчинам",
    children: [
      {
        id: "cat-m-clothing",
        name: "Одежда",
        section: "clothing",
        gender: "male",
        sizeGrid: "clothing-men",
        attributes: "clothing",
        rate: 0.17,
        analog: "Lamoda: Мужчинам / Одежда",
        children: [
          leaf("cat-2-1", "Рубашки", "clothing", "male", { analog: "Lamoda: Рубашки" }),
          leaf("cat-m-tshirts", "Футболки и поло", "clothing", "male", { analog: "Lamoda: Футболки и поло" }),
          leaf("cat-m-knit", "Худи и свитеры", "clothing", "male", { analog: "Lamoda: Худи и свитшоты; Джемперы и свитеры" }),
          leaf("cat-2-2", "Брюки и джинсы", "clothing", "male", { analog: "Lamoda: Брюки, Джинсы, Шорты" }),
          leaf("cat-2-3", "Верхняя одежда", "clothing", "male", { analog: "Lamoda: Верхняя одежда" }),
          leaf("cat-m-sport", "Спортивная одежда", "clothing", "male", { rate: 0.16, analog: "Lamoda: Спорт" }),
          leaf("cat-m-underwear", "Бельё", "clothing", "male", {
            attributes: "lingerie",
            rate: 0.16,
            analog: "Lamoda: Нижнее бельё",
          }),
        ],
      },
      {
        id: "cat-m-shoes",
        name: "Обувь",
        section: "shoes",
        gender: "male",
        sizeGrid: "shoes-men",
        attributes: "shoes",
        rate: 0.16,
        analog: "Lamoda: Мужчинам / Обувь; WB: Обувь / Мужская",
        children: [
          leaf("cat-m-sneakers", "Кроссовки и кеды", "shoes", "male", { analog: "Lamoda: Кроссовки и кеды" }),
          leaf("cat-m-classic", "Туфли и лоферы", "shoes", "male", { analog: "Lamoda: Туфли, Лоферы, Мокасины" }),
          leaf("cat-m-boots", "Ботинки", "shoes", "male", { analog: "Lamoda: Ботинки" }),
          leaf("cat-m-sandals", "Сандалии и шлёпанцы", "shoes", "male", { analog: "Lamoda: Сандалии, Шлёпанцы" }),
        ],
      },
      {
        id: "cat-m-acc",
        name: "Аксессуары",
        section: "accessories",
        gender: "male",
        sizeGrid: "one-size",
        attributes: "accessories",
        rate: 0.18,
        analog: "Lamoda: Мужчинам / Аксессуары",
        children: [
          leaf("cat-m-bags", "Сумки и рюкзаки", "accessories", "male", { analog: "Lamoda: Сумки, Рюкзаки" }),
          leaf("cat-m-jewelry", "Украшения", "accessories", "male", {
            sizeGrid: "jewelry",
            attributes: "jewelry",
            analog: "Lamoda: Украшения, Запонки",
          }),
          leaf("cat-m-hats", "Головные уборы и шарфы", "accessories", "male", { analog: "Lamoda: Головные уборы, Шарфы" }),
          leaf("cat-m-acc-other", "Ремни, очки и другое", "accessories", "male", {
            analog: "Lamoda: Ремни, Очки, Галстуки, Кошельки",
          }),
        ],
      },
    ],
  },
  {
    id: "cat-4",
    name: "Детям",
    section: "kids",
    gender: "kids",
    rate: 0.15,
    analog: "Lamoda: Детям (Девочкам / Мальчикам / Малышам)",
    children: [
      leaf("cat-k-clothing", "Одежда", "kids", "kids", {
        sizeGrid: "kids-clothing",
        attributes: "kids-clothing",
        analog: "Lamoda: Девочкам / Одежда, Мальчикам / Одежда",
      }),
      leaf("cat-k-shoes", "Обувь", "shoes", "kids", {
        sizeGrid: "kids-shoes",
        attributes: "shoes",
        analog: "WB: Обувь / Детская",
      }),
      leaf("cat-k-acc", "Аксессуары", "accessories", "kids", {
        sizeGrid: "one-size",
        attributes: "accessories",
        analog: "Lamoda: Девочкам / Аксессуары",
      }),
      leaf("cat-k-toys", "Игрушки", "kids", "kids", {
        sizeGrid: "one-size",
        attributes: "toys",
        analog: "Lamoda: Детям / Игрушки; WB: Игрушки",
      }),
    ],
  },
  {
    id: "cat-5",
    name: "Красота",
    section: "beauty",
    gender: "unisex",
    sizeGrid: "volume",
    attributes: "beauty",
    rate: 0.12,
    analog: "Lamoda: Красота; Ozon: Красота и здоровье",
    children: [
      leaf("cat-b-face", "Уход за лицом", "beauty", "unisex", { analog: "Lamoda/Ozon: Уход за лицом" }),
      leaf("cat-b-body", "Уход за телом", "beauty", "unisex", { analog: "Lamoda/Ozon: Уход за телом" }),
      leaf("cat-b-hair", "Уход за волосами", "beauty", "unisex", { analog: "Lamoda: Волосы; Ozon: Уход за волосами" }),
      leaf("cat-b-makeup", "Макияж", "beauty", "unisex", { analog: "Lamoda: Макияж; Ozon: Декоративная косметика" }),
      leaf("cat-b-perfume", "Парфюмерия", "beauty", "unisex", {
        attributes: "perfume",
        analog: "Lamoda/WB/Ozon: Парфюмерия",
      }),
    ],
  },
  {
    id: "cat-7",
    name: "Дом и интерьер",
    section: "home",
    gender: "unisex",
    sizeGrid: "one-size",
    attributes: "home",
    rate: 0.15,
    analog: "Lamoda: Дом; Ozon: Дом и сад",
    children: [
      leaf("cat-7-1", "Посуда и керамика", "home", "unisex", {
        attributes: "tableware",
        rate: 0.14,
        analog: "Lamoda: Посуда и аксессуары; Ozon: Посуда",
      }),
      leaf("cat-7-2", "Текстиль", "home", "unisex", { rate: 0.14, analog: "Lamoda/Ozon: Текстиль" }),
      leaf("cat-7-3", "Декор", "home", "unisex", { analog: "Lamoda: Декор; Ozon: Декор и интерьер" }),
      leaf("cat-h-candles", "Свечи и ароматы", "home", "unisex", {
        attributes: "candles",
        analog: "Lamoda: Ароматы для дома; WB: Свечи и подсвечники",
      }),
      leaf("cat-h-light", "Освещение", "home", "unisex", { analog: "Lamoda/WB/Ozon: Освещение" }),
      leaf("cat-h-storage", "Хранение", "home", "unisex", { analog: "Lamoda/Ozon: Хранение вещей" }),
      leaf("cat-h-furniture", "Мебель", "home", "unisex", {
        attributes: "furniture",
        analog: "WB: Мебель (отдельный корень)",
      }),
    ],
  },
]

/** Дерево v1 (до 27.09.2026) — для переноса товаров, сида и тестов миграции. */
export const CATEGORY_TREE_V1: { id: string; name: string; parentId: string | null }[] = [
  { id: "cat-1", name: "Женская одежда", parentId: null },
  { id: "cat-1-1", name: "Платья", parentId: "cat-1" },
  { id: "cat-1-2", name: "Юбки", parentId: "cat-1" },
  { id: "cat-1-3", name: "Блузки", parentId: "cat-1" },
  { id: "cat-1-4", name: "Верхняя одежда", parentId: "cat-1" },
  { id: "cat-2", name: "Мужская одежда", parentId: null },
  { id: "cat-2-1", name: "Рубашки", parentId: "cat-2" },
  { id: "cat-2-2", name: "Брюки", parentId: "cat-2" },
  { id: "cat-2-3", name: "Верхняя одежда", parentId: "cat-2" },
  { id: "cat-3", name: "Обувь", parentId: null },
  { id: "cat-4", name: "Для детей", parentId: null },
  { id: "cat-5", name: "Косметика и уход", parentId: null },
  { id: "cat-6", name: "Здоровье", parentId: null },
  { id: "cat-7", name: "Для дома и интерьера", parentId: null },
  { id: "cat-7-1", name: "Керамика", parentId: "cat-7" },
  { id: "cat-7-2", name: "Текстиль", parentId: "cat-7" },
  { id: "cat-7-3", name: "Декор", parentId: "cat-7" },
  { id: "cat-8", name: "Аксессуары", parentId: null },
  { id: "cat-9", name: "Электроника и техника", parentId: null },
  { id: "cat-10", name: "Для питомцев", parentId: null },
]

/**
 * id узла v1 по имени, как его понимал старый сид: при повторе имени
 * («Верхняя одежда» у женщин и у мужчин) выигрывает последний — так товары
 * демо-каталога и лежали на стенде.
 */
export const v1CategoryIdByName = (name: string): string | undefined => {
  let found: string | undefined
  for (const node of CATEGORY_TREE_V1) if (node.name === name) found = node.id
  return found
}

/** Узлы дерева v1, которые уходят в архив (не удаляются: на них ставки и история). */
export const ARCHIVED_V1_CATEGORY_IDS = ["cat-3", "cat-6", "cat-8", "cat-9", "cat-10"] as const

/** Плоский список узлов v2 с родителем, уровнем и порядком — как лежит в таблице. */
export type FlatTreeNode = Omit<CategoryTreeNode, "children"> & {
  parentId: string | null
  level: 1 | 2 | 3
  sortOrder: number
  isLeaf: boolean
}

export const flattenCategoryTreeV2 = (tree: CategoryTreeNode[] = CATEGORY_TREE_V2): FlatTreeNode[] => {
  const out: FlatTreeNode[] = []
  let order = 0
  const visit = (node: CategoryTreeNode, parentId: string | null, level: 1 | 2 | 3) => {
    const { children, ...rest } = node
    out.push({ ...rest, parentId, level, sortOrder: (order += 10), isLeaf: !children?.length })
    for (const child of children ?? []) visit(child, node.id, (level + 1) as 1 | 2 | 3)
  }
  for (const root of tree) visit(root, null, 1)
  return out
}

// ---------------------------------------------------------------------------
// Перенос товаров из дерева v1
// ---------------------------------------------------------------------------

/** Вид товара, распознанный по названию. */
export type ProductKind =
  | "lingerie"
  | "perfume"
  | "makeup"
  | "face"
  | "body"
  | "hair"
  | "jewelry"
  | "bags"
  | "hats"
  | "accother"
  | "boots"
  | "classic"
  | "sneakers"
  | "sandals"
  | "dress"
  | "skirt"
  | "outerwear"
  | "knit"
  | "tshirt"
  | "shirt"
  | "sport"
  | "pants"
  | "toys"
  | "lighting"
  | "candles"
  | "textile"
  | "furniture"
  | "storage"
  | "decor"
  | "tableware"

/** Граница слова, одинаково понятная JavaScript и PostgreSQL (`~*`). */
const B = "(^|[^a-zа-яё])"
const E = "([^a-zа-яё]|$)"
const words = (list: string): string => `${B}(${list})${E}`

/**
 * Правила распознавания вида по названию: порядок = приоритет, первое
 * совпадение выигрывает. Шаблоны пишутся так, чтобы одинаково работать в JS
 * (`new RegExp(p, "i")`) и в PostgreSQL (`name ~* p`): миграция 134 несёт ту же
 * таблицу, тест сверяет результат.
 *
 * `audience` — вид, который сам говорит об аудитории (запонки — мужское,
 * бюстгальтер — женское), если в названии нет явного «Women/Men».
 */
export const PRODUCT_KIND_RULES: { kind: ProductKind; pattern: string; audience?: "female" | "male" }[] = [
  {
    kind: "lingerie",
    pattern: `${words("bras?|briefs?|bikini|boxers?|panty|panties|lingerie|hipster|boy shorts|swimsuit|swimwear")}|бюстгальтер|трусы|купальник|нижнее бель`,
  },
  { kind: "perfume", pattern: `${words("eau de|edt|edp|parfum|perfume")}|духи|парфюм|туалетная вода` },
  {
    kind: "makeup",
    pattern: `${words("lip|lips|lipstick|concealer|eyeshadow|mascara|foundation|blush|eyeliner")}|помад|тени для|тушь|консилер|румян|тональн`,
  },
  { kind: "face", pattern: `${words("face|serum|facial")}|для лица|сыворотк|тоник` },
  { kind: "body", pattern: `${words("body|soap|scrub|lotion|shower")}|для тела|мыло|скраб|гель для душа` },
  { kind: "hair", pattern: `${words("hair|shampoo|фен")}|шампун|для волос` },
  { kind: "jewelry", pattern: `${words("cufflinks?")}|запонк`, audience: "male" },
  {
    kind: "jewelry",
    pattern: `${words("earrings?|necklaces?|bracelets?|bangles?|rings?|pendants?|chains?|anklets?|nosepin|tikka|mangalsutra|cufflinks?|brooch(es)?|jewell?ery")}|серьг|кольц|браслет|колье|подвеск|цепочк|брошь|запонк|украшен|бижутер`,
  },
  {
    kind: "bags",
    pattern: `${words("bags?|backpacks?|trolley|wallets?|clutch(es)?|tote|shopper")}|сумк|рюкзак|кошел|клатч|шоппер`,
  },
  {
    kind: "hats",
    pattern: `${words("baseball caps?|snapbacks?|hats?|beanies?|scarf|scarves|stoles?|dupattas?|bandanas?")}|шапк|кепк|панам|шарф|платок|палантин|берет|бандан`,
  },
  {
    kind: "accother",
    pattern: `${words("belts?|sunglasses|ties|watch(es)?|keychains?|phone case|очки|часы")}|ремень|ремни|галстук|брелок|чехол`,
  },
  { kind: "boots", pattern: `${words("boots?|booties")}|ботин|сапог|челси|угги` },
  {
    kind: "classic",
    pattern: `${words("loafers?|derbys?|derbies|oxfords?|brogues?|heels|pumps|flats|ballerinas?|mules|formal slip-?ons?")}|туфл|лофер|балетк|мюли|мокасин`,
  },
  { kind: "sneakers", pattern: `${words("sneakers?|trainers|slip-?ons?|slip-?on shoes")}|кроссовк|кеды|слипон` },
  { kind: "sandals", pattern: `${words("sandals?|flip-?flops?|slides")}|сандал|шл[её]пан|босонож|сланц` },
  { kind: "sport", pattern: `${words("sports?|gym|yoga")}|спортивн` },
  { kind: "dress", pattern: `${words("dress(es)?|gowns?|sarees?")}|плать|сарафан` },
  { kind: "skirt", pattern: `${words("skirts?")}|юбк` },
  {
    kind: "outerwear",
    pattern: `${words("jackets?|coats?|blazers?|bombers?|parkas?|sherwani|suits?")}|костюм|куртк|пальто|пиджак|жакет|плащ|пуховик|бомбер|ветровк`,
  },
  {
    kind: "knit",
    pattern: `${words("sweaters?|sweatshirts?|hoodies?|hooded|cardigans?|jumpers?|pullovers?")}|свитер|худи|свитшот|кардиган|толстовк|джемпер`,
  },
  { kind: "tshirt", pattern: `${words("t-?shirts?|tees?|polos?|tops?|tank|топ|топы|поло")}|футболк|майк|лонгслив` },
  { kind: "shirt", pattern: `${words("shirts?|blouses?|kurtas?|tunics?")}|рубашк|блуз|туник|сорочк` },
    {
    kind: "pants",
    pattern: `${words("jeans|trousers|pants|shorts|palazzos?|chinos|joggers|leggings|tights|trackpants")}|брюк|джинс|шорт|легинс|лосин`,
  },
  { kind: "toys", pattern: `${words("toys?|plush")}|игрушк|плюшев|конструктор` },
  {
    kind: "lighting",
    pattern: `${words("lamps?|lights?|lighting|chandelier")}|ламп|светильник|торшер|ночник|гирлянд|люстр`,
  },
  { kind: "candles", pattern: `${words("candles?|diffusers?")}|свеч|диффузор|аромасаше` },
  {
    kind: "textile",
    pattern: `${words("bedsheets?|placemats?|dohar|blankets?|pillow covers?|cushion covers?|towels?|curtains?|napkins?")}|плед|подушк|полотенц|скатерт|салфетк|постельн|штор|покрывал`,
  },
  {
    kind: "furniture",
    pattern: `${words("bed|beds|sofa|sofas|chairs?|table|tables|bedside|sink|cabinet|wardrobe|shelf|shelving|dresser|стол|столы|столик|стул|стулья")}|мебел|шкаф|комод|кресл|диван|кровать|тумб|стеллаж`,
  },
  { kind: "storage", pattern: `${words("bins?|baskets?|jars?|racks?|organi[sz]ers?|storage")}|корзин|органайзер|хранени|короб` },
  {
    kind: "decor",
    pattern: `${words("frames?|showpiece|vases?|plants?|swing|decoration|poster|mirror|figurine")}|декор|рамк|ваза|постер|картин|статуэт|панно|кашпо`,
  },
  {
    kind: "tableware",
    pattern: `${words("ceramic|bowls?|mugs?|cups?|plates?|glass(es)?|spoons?|forks?|knife|knives|pans?|woks?|pots?|trays?|kulladhs?|teapot|spatula|whisk|tongs|turner|peeler|grater|sieve|strainer|rolling pin|chopping board")}|посуд|кружк|чашк|тарелк|миск|керамик|сервиз|бокал|стакан|чайник`,
  },
]

const KIDS_PATTERN = `${words("boys|girls|kids|kid|baby|infants?|toddler")}|детск|для мальчик|для девоч|малыш`
const FEMALE_PATTERN = `${words("women|woman|womens|ladies|her|girl")}|женск|женщин|для неё|для нее`
const MALE_PATTERN = `${words("men|man|mens|him")}|мужск|мужчин|для него`
const UNISEX_PATTERN = `${words("unisex")}|унисекс`

/** Шаблоны аудитории — миграция несёт их же. */
export const AUDIENCE_PATTERNS = {
  kids: KIDS_PATTERN,
  female: FEMALE_PATTERN,
  male: MALE_PATTERN,
  unisex: UNISEX_PATTERN,
} as const

export type Audience = "female" | "male" | "kids"

/** Аудитория по умолчанию для узла v1, когда название о ней молчит. */
export const V1_DEFAULT_AUDIENCE: Record<string, Audience> = {
  "cat-1": "female",
  "cat-1-1": "female",
  "cat-1-2": "female",
  "cat-1-3": "female",
  "cat-1-4": "female",
  "cat-2": "male",
  "cat-2-1": "male",
  "cat-2-2": "male",
  "cat-2-3": "male",
  "cat-4": "kids",
}

/** Куда ложится вид товара для каждой аудитории. `beauty/home` от аудитории не зависят. */
export const KIND_TARGETS: Record<ProductKind, Record<Audience, string>> = {
  lingerie: { female: "cat-w-lingerie", male: "cat-m-underwear", kids: "cat-k-clothing" },
  perfume: { female: "cat-b-perfume", male: "cat-b-perfume", kids: "cat-b-perfume" },
  makeup: { female: "cat-b-makeup", male: "cat-b-makeup", kids: "cat-b-makeup" },
  face: { female: "cat-b-face", male: "cat-b-face", kids: "cat-b-face" },
  body: { female: "cat-b-body", male: "cat-b-body", kids: "cat-b-body" },
  hair: { female: "cat-b-hair", male: "cat-b-hair", kids: "cat-b-hair" },
  jewelry: { female: "cat-w-jewelry", male: "cat-m-jewelry", kids: "cat-k-acc" },
  bags: { female: "cat-w-bags", male: "cat-m-bags", kids: "cat-k-acc" },
  hats: { female: "cat-w-hats", male: "cat-m-hats", kids: "cat-k-acc" },
  accother: { female: "cat-w-acc-other", male: "cat-m-acc-other", kids: "cat-k-acc" },
  boots: { female: "cat-w-boots", male: "cat-m-boots", kids: "cat-k-shoes" },
  classic: { female: "cat-w-heels", male: "cat-m-classic", kids: "cat-k-shoes" },
  sneakers: { female: "cat-w-sneakers", male: "cat-m-sneakers", kids: "cat-k-shoes" },
  sandals: { female: "cat-w-sandals", male: "cat-m-sandals", kids: "cat-k-shoes" },
  dress: { female: "cat-1-1", male: "cat-m-clothing", kids: "cat-k-clothing" },
  skirt: { female: "cat-1-2", male: "cat-m-clothing", kids: "cat-k-clothing" },
  outerwear: { female: "cat-1-4", male: "cat-2-3", kids: "cat-k-clothing" },
  knit: { female: "cat-w-knit", male: "cat-m-knit", kids: "cat-k-clothing" },
  tshirt: { female: "cat-w-tshirts", male: "cat-m-tshirts", kids: "cat-k-clothing" },
  shirt: { female: "cat-1-3", male: "cat-2-1", kids: "cat-k-clothing" },
  sport: { female: "cat-w-sport", male: "cat-m-sport", kids: "cat-k-clothing" },
  pants: { female: "cat-w-pants", male: "cat-2-2", kids: "cat-k-clothing" },
  toys: { female: "cat-k-toys", male: "cat-k-toys", kids: "cat-k-toys" },
  lighting: { female: "cat-h-light", male: "cat-h-light", kids: "cat-h-light" },
  candles: { female: "cat-h-candles", male: "cat-h-candles", kids: "cat-h-candles" },
  textile: { female: "cat-7-2", male: "cat-7-2", kids: "cat-7-2" },
  furniture: { female: "cat-h-furniture", male: "cat-h-furniture", kids: "cat-h-furniture" },
  storage: { female: "cat-h-storage", male: "cat-h-storage", kids: "cat-h-storage" },
  decor: { female: "cat-7-3", male: "cat-7-3", kids: "cat-7-3" },
  tableware: { female: "cat-7-1", male: "cat-7-1", kids: "cat-7-1" },
}

/**
 * Куда товар падает, если по названию вид не распознан. Для узлов, которые
 * сохранились, — они же; для упразднённых — ближайший узел второго уровня
 * (такой товар остаётся на витрине и попадает в список «разобрать вручную»).
 */
export const V1_FALLBACK: Record<string, Record<Audience, string>> = {
  "cat-1": { female: "cat-w-clothing", male: "cat-m-clothing", kids: "cat-k-clothing" },
  "cat-1-1": { female: "cat-1-1", male: "cat-m-clothing", kids: "cat-k-clothing" },
  "cat-1-2": { female: "cat-1-2", male: "cat-m-clothing", kids: "cat-k-clothing" },
  "cat-1-3": { female: "cat-1-3", male: "cat-2-1", kids: "cat-k-clothing" },
  "cat-1-4": { female: "cat-1-4", male: "cat-2-3", kids: "cat-k-clothing" },
  "cat-2": { female: "cat-w-clothing", male: "cat-m-clothing", kids: "cat-k-clothing" },
  "cat-2-1": { female: "cat-1-3", male: "cat-2-1", kids: "cat-k-clothing" },
  "cat-2-2": { female: "cat-w-pants", male: "cat-2-2", kids: "cat-k-clothing" },
  "cat-2-3": { female: "cat-1-4", male: "cat-2-3", kids: "cat-k-clothing" },
  "cat-3": { female: "cat-w-shoes", male: "cat-m-shoes", kids: "cat-k-shoes" },
  "cat-4": { female: "cat-k-clothing", male: "cat-k-clothing", kids: "cat-k-clothing" },
  "cat-5": { female: "cat-5", male: "cat-5", kids: "cat-5" },
  "cat-6": { female: "cat-5", male: "cat-5", kids: "cat-5" },
  "cat-7": { female: "cat-7", male: "cat-7", kids: "cat-7" },
  "cat-7-1": { female: "cat-7-1", male: "cat-7-1", kids: "cat-7-1" },
  "cat-7-2": { female: "cat-7-2", male: "cat-7-2", kids: "cat-7-2" },
  "cat-7-3": { female: "cat-7-3", male: "cat-7-3", kids: "cat-7-3" },
  "cat-8": { female: "cat-w-acc", male: "cat-m-acc", kids: "cat-k-acc" },
  "cat-9": { female: "cat-7", male: "cat-7", kids: "cat-7" },
  "cat-10": { female: "cat-7", male: "cat-7", kids: "cat-7" },
}

export const V1_CATEGORY_IDS = Object.keys(V1_FALLBACK)

export type ArchivedV1CategoryId = (typeof ARCHIVED_V1_CATEGORY_IDS)[number]

/**
 * Преемник архивного узла v1 для ССЫЛОК, ЛЕНТ и ПОДБОРОК — по аудитории.
 *
 * Та же таблица, что перенос товаров (`V1_FALLBACK`), с одним отличием:
 * у «Электроники и техники» и «Для питомцев» смыслового преемника нет —
 * их товары легли в «Дом и интерьер» только чтобы не пропасть, а подменять
 * ссылку или ленту «электроника» «Домом» значит показать не то, что просили.
 * Шлюз отдаёт эту таблицу архивным узлам (`successorId`), миграция 135 по ней
 * переводит сохранённые ленты покупателей.
 */
export const ARCHIVED_V1_SUCCESSORS: Record<ArchivedV1CategoryId, Record<Audience, string> | null> = {
  "cat-3": V1_FALLBACK["cat-3"] as Record<Audience, string>,
  "cat-6": V1_FALLBACK["cat-6"] as Record<Audience, string>,
  "cat-8": V1_FALLBACK["cat-8"] as Record<Audience, string>,
  "cat-9": null,
  "cat-10": null,
}

/** Аудитория, когда её не понять из контекста: «при неясности — Женщинам». */
export const DEFAULT_SUCCESSOR_AUDIENCE: Audience = "female"

/** Преемник архивного узла v1 для аудитории; `null` — преемника нет или узел не из v1. */
export const archivedV1Successor = (id: string, audience: Audience = DEFAULT_SUCCESSOR_AUDIENCE): string | null =>
  (ARCHIVED_V1_SUCCESSORS as Record<string, Record<Audience, string> | null>)[id]?.[audience] ?? null

const test = (pattern: string, text: string) => new RegExp(pattern, "i").test(text)

/**
 * Хвост названия после «with / с / со» описывает комплектацию, а не саму вещь:
 * «Dress with Belt» — платье, а не ремень; «Платье с поясом» — тоже. Поэтому вид
 * ищется сначала в «голове» названия и только потом во всём названии.
 */
export const NAME_TAIL_PATTERN = "\\s(with|с|со)\\s.*$"
export const nameHead = (name: string): string => name.replace(new RegExp(NAME_TAIL_PATTERN, "i"), "")

const findKind = (text: string) => PRODUCT_KIND_RULES.find((rule) => test(rule.pattern, text))

export const productKindOf = (name: string): { kind: ProductKind; audience?: "female" | "male" } | undefined =>
  findKind(nameHead(name)) ?? findKind(name)

/**
 * Аудитория товара: явное «детское» в названии → явное «женское/мужское» →
 * аудитория, которую подсказывает сам вид (запонки) → аудитория старого узла →
 * женская (большая часть ассортимента площадки; унисекс помечается отдельно).
 */
export const audienceOf = (oldCategoryId: string, name: string, kindAudience?: "female" | "male"): Audience => {
  if (test(KIDS_PATTERN, name)) return "kids"
  if (test(FEMALE_PATTERN, name)) return "female"
  if (test(MALE_PATTERN, name)) return "male"
  if (oldCategoryId === "cat-4") return "kids"
  if (kindAudience) return kindAudience
  return V1_DEFAULT_AUDIENCE[oldCategoryId] ?? "female"
}

/**
 * Новая категория для товара из узла дерева v1. Для узлов вне v1 (уже v2 или
 * созданных вручную) возвращает `undefined` — такие товары не трогаются.
 */
export const classifyV1Product = (oldCategoryId: string, name: string): string | undefined => {
  const fallback = V1_FALLBACK[oldCategoryId]
  if (!fallback) return undefined
  const match = productKindOf(name)
  const audience = audienceOf(oldCategoryId, name, match?.audience)
  return match ? KIND_TARGETS[match.kind][audience] : fallback[audience]
}

/** Товар помечается «унисекс» (поправка пола), если это сказано в названии. */
export const isUnisexName = (name: string): boolean => test(UNISEX_PATTERN, name)
